import { LitElement } from '@hh/global.js'

// TO USE THIS COMPONENT:
/*
  <card-carousel
    autoplay
    scrollbar
    swiper-container-id="container--{{ section.id }}"
    cursor-id="cursor"
  >
    <div id="container--{{ section.id }}" class="swiper">
      <div class="swiper-wrapper swiper-no-swiping">
        <div class="swiper-slide"></div>
      </div>
      <div class="swiper-button-prev"></div>
      <div class="swiper-button-next"></div>
      <div class="swiper-scrollbar"></div>
    </div>
  </card-carousel>
*/
// Copy section-collection-carousel.css and customize for your carousel implementation

class CardCarousel extends LitElement {
  static properties = {
    autoplay: { type: Boolean, default: false },
    scrollbar: { type: Boolean, default: false },
    swiperContainerId: { type: String, attribute: 'swiper-container-id' },
    cursorId: { type: String, attribute: 'cursor-id' },
  }

  constructor() {
    super()

    this.isDragStart = false
    this.isDragging = false
    this.prevPageX
    this.prevScrollLeft
    this.positionDiff
    this.timeout
    this.interval = 150
    this.clientScreenWidth = document.documentElement.clientWidth / 2
    this.initialDragPosition = 0
  }

  connectedCallback() {
    super.connectedCallback()
    this.carouselContainer = this.querySelector(`#${this.swiperContainerId}`)
    this.cursorControl = this.querySelector(`#${this.cursorId}`)

    this.initSlider()

    window.addEventListener('resize', this.getCurrentClientScreenWidth)

    if (this.cursorControl) {
      this.carouselContainer.addEventListener(
        'mouseenter',
        this.handleMouseEnter
      )
      this.carouselContainer.addEventListener(
        'mouseleave',
        this.handleMouseLeave
      )
      this.carouselContainer.addEventListener('mousemove', this.handleMouseMove)
    }

    this.carouselContainer.addEventListener('mousedown', this.dragStart)
    this.carouselContainer.addEventListener('touchstart', this.dragStart)

    this.carouselContainer.addEventListener('mousemove', this.dragging)
    this.carouselContainer.addEventListener('touchmove', this.dragging)

    this.carouselContainer.addEventListener('mouseup', this.dragStop)
    this.carouselContainer.addEventListener('touchend', this.dragStop)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('resize', this.getCurrentClientScreenWidth)
  }

  initSlider() {
    let config = {
      effect: 'coverflow',
      loop: true,
      slidesPerView: 1.7,
      spaceBetween: 20,
      centeredSlides: true,
      speed: 200,
      coverflowEffect: {
        rotate: 0,
        stretch: 0,
        depth: 120,
        modifier: 1,
        scale: 0.9,
        slideShadows: false,
      },
      breakpoints: {
        480: {
          slidesPerView: 3,
        },
        750: {
          slidesPerView: 'auto',
        },
      },
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
    }

    if (this.autoplay) {
      const autoplay = {
        autoplay: {
          pauseOnMouseEnter: false,
        },
      }
      Object.assign(config, autoplay)
    }

    // if (this.scrollbar) {
    //   const scrollbar = {
    //     scrollbar: {
    //       el: ".swiper-scrollbar",
    //       hide: false,
    //     },
    //   }
    //   Object.assign(config, scrollbar)
    // }

    this.swiper = new Swiper(`#${this.swiperContainerId}`, config)
    this.initScrollbar()
  }

  getCurrentClientScreenWidth() {
    this.clientScreenWidth = document.documentElement.clientWidth / 2
    if (typeof this.initScrollbar === 'function') {
      this.initScrollbar()
    }
  }

  isMobile() {
    const regex =
      /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
    return regex.test(navigator.userAgent)
  }

  initScrollbar() {
    if (this.scrollbar) {
      const scrollbarContainerEl = this.querySelector('.swiper-scrollbar')
      const scrollbarBarEl = this.querySelector(
        '.swiper-scrollbar .swiper-scrollbar-drag'
      )
      const scrollbarFraction =
        scrollbarContainerEl.offsetWidth /
        this.querySelectorAll('.swiper-slide').length

      scrollbarBarEl.style.width = `${scrollbarFraction}px`

      // Clear any old event listeners
      this.swiper.off('slideChangeTransitionStart', () => {
        scrollbarBarEl.style.left = `${
          scrollbarFraction * Math.max(0, this.swiper.realIndex)
        }px`
      })

      // Create new listener
      this.swiper.on('slideChangeTransitionStart', () => {
        scrollbarBarEl.style.left = `${
          scrollbarFraction * Math.max(0, this.swiper.realIndex)
        }px`
      })
    }
  }

  handleMouseEnter = (e) => {
    if (!this.isMobile()) {
      this.cursorControl.style.display = 'block'
    }
  }

  handleMouseLeave = (e) => {
    this.cursorControl.style.display = 'none'
  }

  handleMouseMove = (e) => {
    this.cursorControl.style.transform =
      'translate3d(' + (e.pageX - 30) + 'px, ' + (e.offsetY - 10) + 'px, 0)'
    if (e.pageX < this.clientScreenWidth) {
      this.cursorControl.classList.remove('right')
      this.cursorControl.classList.add('left')
    } else {
      this.cursorControl.classList.remove('left')
      this.cursorControl.classList.add('right')
    }
  }

  dragStart = (e) => {
    this.isDragStart = true
    this.prevPageX = e.pageX || e.touches[0].pageX
    this.initialDragPosition = this.prevPageX
    this.positionDiff = (e.pageX || e.touches[0].pageX) - this.prevPageX
    this.prevScrollLeft = this.carouselContainer.scrollLeft
    clearTimeout(this.timeout)
  }

  dragging = (e) => {
    if (!this.isDragStart) return
    e.preventDefault()
    this.isDragging = true
    this.prevPageX = e.pageX || e.touches[0].pageX
    this.positionDiff = (e.pageX || e.touches[0].pageX) - this.prevPageX
    this.carouselContainer.scrollLeft = this.prevScrollLeft - this.positionDiff
    clearTimeout(this.timeout)
  }

  dragStop = (e) => {
    if (this.initialDragPosition === 0) return

    if (this.initialDragPosition == this.prevPageX) {
      if (this.initialDragPosition < this.clientScreenWidth) {
        this.swiper.slidePrev()
      } else {
        this.swiper.slideNext()
      }
    } else {
      if (this.initialDragPosition < this.prevPageX) {
        this.swiper.slidePrev()
      } else {
        this.swiper.slideNext()
      }
    }

    this.isDragStart = false
    this.initialDragPosition = 0
    this.carouselContainer.classList.remove('dragging')
    if (!this.isDragging) return
    this.isDragging = false
  }

  createRenderRoot() {
    return this
  }
}

customElements.define('card-carousel', CardCarousel)
